class ProductConfigurator {
  constructor(container) {
    this.container = container;
    this.basePrice = parseFloat(container.dataset.basePrice) || 0;
    this.mainVariantId = container.dataset.mainVariantId;
    this.selects = container.querySelectorAll('.configurator-select');
    this.checkboxes = container.querySelectorAll('.configurator-checkbox input[type="checkbox"]');
    this.totalDisplay = container.querySelector('[data-total-price]');
    
    this.productForm = container.closest('form[action*="/cart/add"]') || 
                       document.querySelector('form[action*="/cart/add"]');
    
    this.init();
  }

  init() {
    this.selects.forEach(select => {
      select.addEventListener('change', () => {
        this.updateDescription(select);
        this.calculateTotal();
      });
    });

    this.checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.calculateTotal());
    });

    if (this.productForm) {
      this.productForm.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    this.interceptAjaxAddToCart();
    this.calculateTotal();
  }

  interceptAjaxAddToCart() {
    const addToCartSelectors = [
      '[data-add-to-cart]',
      '.product-form__submit',
      '.add-to-cart',
      'button[name="add"]',
      '.btn-add-to-cart'
    ];

    addToCartSelectors.forEach(selector => {
      const buttons = document.querySelectorAll(selector);
      buttons.forEach(button => {
        if (button.closest('form') === this.productForm || 
            button.closest('.product') || 
            button.closest('[data-product]')) {
          button.addEventListener('click', (e) => this.handleAddToCartClick(e));
        }
      });
    });
  }

  handleAddToCartClick(e) {
    const addonVariantIds = this.getSelectedAddonVariantIds();
    
    if (addonVariantIds.length === 0) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    
    this.addToCartWithAddons();
  }

  handleSubmit(e) {
    const addonVariantIds = this.getSelectedAddonVariantIds();
    
    if (addonVariantIds.length === 0) {
      return;
    }

    e.preventDefault();
    this.addToCartWithAddons();
  }

  getSelectedAddonVariantIds() {
    const variantIds = [];

    this.selects.forEach(select => {
      const selectedOption = select.options[select.selectedIndex];
      const variantId = selectedOption.dataset.addonVariantId;
      if (variantId && variantId !== '') {
        variantIds.push(variantId);
      }
    });

    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const variantId = checkbox.dataset.addonVariantId;
        if (variantId && variantId !== '') {
          variantIds.push(variantId);
        }
      }
    });

    return variantIds;
  }

  getLineItemProperties() {
    const properties = {};

    this.selects.forEach(select => {
      const name = select.name.replace('properties[', '').replace(']', '');
      properties[name] = select.value;
    });

    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const name = checkbox.name.replace('properties[', '').replace(']', '');
        properties[name] = checkbox.value;
      }
    });

    return properties;
  }

  generateGroupId() {
    return 'cfg_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  async addToCartWithAddons() {
    const addonVariantIds = this.getSelectedAddonVariantIds();
    const properties = this.getLineItemProperties();
    
    // Use the variant ID set by Liquid based on customer type (MAP or Government)
    // This is already set correctly in the constructor from data-main-variant-id
    const mainVariantId = this.mainVariantId;

    // Generate unique group ID to link main product with its addons
    const groupId = this.generateGroupId();

    const items = [];

    // Add main product with group ID
    items.push({
      id: parseInt(mainVariantId),
      quantity: 1,
      properties: {
        ...properties,
        '_group_id': groupId
      }
    });

    // Get the product title
    const productTitle = document.querySelector('.product__title, .product-title, h1.title')?.textContent?.trim() || 'Main Product';

    // Add all addon products with same group ID
    addonVariantIds.forEach(variantId => {
      items.push({
        id: parseInt(variantId),
        quantity: 1,
        properties: {
          '_addon_for': productTitle,
          '_group_id': groupId,
          '_hidden': 'true'
        }
      });
    });

    try {
      this.setLoadingState(true);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items: items })
      });

      if (!response.ok) {
        throw new Error('Failed to add to cart');
      }

      const result = await response.json();
      this.triggerCartUpdate();
      this.handleAddToCartSuccess();

    } catch (error) {
      console.error('Error adding to cart:', error);
      this.handleAddToCartError(error);
    } finally {
      this.setLoadingState(false);
    }
  }

  setLoadingState(isLoading) {
    const submitButton = this.productForm?.querySelector('[type="submit"], .add-to-cart, [data-add-to-cart]');
    if (submitButton) {
      submitButton.disabled = isLoading;
      if (isLoading) {
        submitButton.dataset.originalText = submitButton.textContent;
        submitButton.textContent = 'Adding...';
      } else {
        submitButton.textContent = submitButton.dataset.originalText || 'Add to Cart';
      }
    }
  }

  triggerCartUpdate() {
    document.dispatchEvent(new CustomEvent('cart:updated'));
    
    if (typeof window.cart !== 'undefined' && typeof window.cart.refresh === 'function') {
      window.cart.refresh();
    }

    document.dispatchEvent(new CustomEvent('ajaxProduct:added'));
  }

  handleAddToCartSuccess() {
    window.location.href = '/cart';
  }

  handleAddToCartError(error) {
    alert('There was an error adding to cart. Please try again.');
  }

  updateDescription(select) {
    const groupId = select.dataset.groupId;
    const descriptionEl = this.container.querySelector(`[data-description-for="${groupId}"]`);
    
    if (!descriptionEl) return;

    const selectedOption = select.options[select.selectedIndex];
    const description = selectedOption.dataset.description;

    if (description) {
      descriptionEl.textContent = description;
      descriptionEl.style.display = '';
    } else {
      descriptionEl.style.display = 'none';
    }
  }

  calculateTotal() {
    let total = this.basePrice;

    this.selects.forEach(select => {
      const selectedOption = select.options[select.selectedIndex];
      const adjustment = parseFloat(selectedOption.dataset.price) || 0;
      total += adjustment;
    });

    this.checkboxes.forEach(checkbox => {
      if (checkbox.checked) {
        const adjustment = parseFloat(checkbox.dataset.price) || 0;
        total += adjustment;
      }
    });

    this.updateDisplay(total);
    this.updateMainPrice(total);
  }

  updateDisplay(total) {
    if (this.totalDisplay) {
      this.totalDisplay.textContent = this.formatMoney(total);
    }
  }

  updateMainPrice(total) {
    const mainPriceSelectors = [
      '.price__regular .price-item',
      '.product-price',
      '[data-product-price]',
      '.product__price',
      '.price-item--regular'
    ];

    for (const selector of mainPriceSelectors) {
      const mainPrice = document.querySelector(selector);
      if (mainPrice) {
        mainPrice.textContent = this.formatMoney(total);
        break;
      }
    }
  }

  formatMoney(amount) {
    const formatted = parseFloat(amount).toFixed(2);
    return '$' + formatted.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  const configurators = document.querySelectorAll('.product-configurator');
  configurators.forEach(container => new ProductConfigurator(container));
});

// Reinitialize on section reload (theme editor)
document.addEventListener('shopify:section:load', (event) => {
  const configurators = event.target.querySelectorAll('.product-configurator');
  configurators.forEach(container => new ProductConfigurator(container));
});
